import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { toast } from 'react-toastify';
import {
  TableRow,
  TableCell,
  Chip,
  Typography,
  TextField,
  Box,
  Link as LinkMaterial,
} from '@material-ui/core';
import {
  ReceiptOutlined,
  Code as CodeIcon,
  FileCopyOutlined,
  OpenInNew as OpenInNewIcon,
} from '@material-ui/icons';

import Table from '~/components/Table/Table';
import TablePagination from '~/components/Table/TablePagination';
import tableStyles from '~/components/Table/Table/styles';
import LinearProgress from '~/components/LinearProgress';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import Paper from '~/components/Paper';
import Button from '~/components/Button';
import Dialog from '~/components/Dialog';
import Request from '~/providers/Request';
import { showPerson } from '~/config/routes';

moment.locale('pt-br');

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'iniciada', label: 'Iniciada' },
  { value: 'aguardando_integracao', label: 'Aguardando integração' },
  { value: 'finalizada', label: 'Finalizada' },
  { value: 'cancelada', label: 'Cancelada' },
  { value: 'erro', label: 'Erro' },
];

const STATUS_META = {
  iniciada: { label: 'Iniciada', color: '#9e9e9e' },
  aguardando_integracao: { label: 'Aguardando integração', color: '#ff9800' },
  finalizada: { label: 'Finalizada', color: '#4caf50' },
  cancelada: { label: 'Cancelada', color: '#607d8b' },
  erro: { label: 'Erro', color: '#f44336' },
};

const formatDate = (d) => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '-');

const MevoPrescricoes = () => {
  const classesTable = tableStyles();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [patientSearch, setPatientSearch] = useState('');

  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [jsonData, setJsonData] = useState(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      try {
        const params = { page: page + 1, per_page: rowsPerPage };
        if (statusFilter) params.status = statusFilter;
        if (patientSearch.trim()) params.patient_name = patientSearch.trim();
        const resp = await Request.get(
          '/admin/mevo/prescricoes/relatorio',
          params,
          { normalizeJSON: false }
        );
        if (!active) return;
        setData(resp.data || []);
        setTotal((resp.meta && resp.meta.total) || 0);
      } catch (e) {
        if (active) toast.error('Erro ao buscar as receitas Mevo.');
      } finally {
        if (active) setLoading(false);
      }
    };
    const timer = setTimeout(run, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [page, rowsPerPage, statusFilter, patientSearch]);

  const statusChip = (s) => {
    const m = STATUS_META[s] || { label: s || '-', color: '#9e9e9e' };
    return (
      <Chip
        label={m.label}
        size="small"
        style={{ backgroundColor: m.color, color: 'white' }}
      />
    );
  };

  const openJson = async (id) => {
    setJsonOpen(true);
    setJsonLoading(true);
    setJsonData(null);
    try {
      const resp = await Request.get(
        `/admin/mevo/prescricoes/${id}/json`,
        {},
        { normalizeJSON: false }
      );
      setJsonData(resp);
    } catch (e) {
      toast.error('Erro ao carregar o JSON.');
    } finally {
      setJsonLoading(false);
    }
  };

  const copyJson = (obj) => {
    try {
      navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
      toast.success('JSON copiado.');
    } catch (e) {
      toast.error('Não foi possível copiar.');
    }
  };

  const jsonBox = (obj) => (
    <Box
      style={{
        maxHeight: 280,
        overflow: 'auto',
        background: '#0f172a',
        color: '#e2e8f0',
        padding: 12,
        borderRadius: 4,
        fontSize: 12,
      }}
    >
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {obj ? JSON.stringify(obj, null, 2) : '—'}
      </pre>
    </Box>
  );

  return (
    <Paper
      Icon={ReceiptOutlined}
      iconColor="blue"
      title="Receitas Mevo"
      subtitle="Status das prescrições e o JSON enviado à Mevo"
    >
      <GridContainer style={{ marginTop: 10 }} alignItems="center">
        <GridItem xs={12} sm={4}>
          <TextField
            select
            SelectProps={{ native: true }}
            fullWidth
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setPage(0);
              setStatusFilter(e.target.value);
            }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </TextField>
        </GridItem>
        <GridItem xs={12} sm={4}>
          <TextField
            fullWidth
            label="Paciente"
            placeholder="Buscar por nome"
            value={patientSearch}
            onChange={(e) => {
              setPage(0);
              setPatientSearch(e.target.value);
            }}
          />
        </GridItem>
      </GridContainer>

      <br />

      <Table
        loading={loading}
        emptyData={data.length === 0}
        columns={
          <>
            <TableCell className={classesTable.cell}>#</TableCell>
            <TableCell className={classesTable.cell}>Paciente</TableCell>
            <TableCell className={classesTable.cell}>Médico</TableCell>
            <TableCell className={classesTable.cell}>Status</TableCell>
            <TableCell className={classesTable.cell}>Cód. validação</TableCell>
            <TableCell className={classesTable.cell}>Criada em</TableCell>
            <TableCell className={classesTable.cell}>JSON</TableCell>
          </>
        }
      >
        {data.map((item) => (
          <TableRow key={item.id} hover className={classesTable.row}>
            <TableCell>{item.id}</TableCell>
            <TableCell>
              {item.patient_id ? (
                <Chip
                  label={item.patient_name || item.patient_id}
                  component={LinkMaterial}
                  href={showPerson.reverse({ id: item.patient_id })}
                  style={{ cursor: 'pointer' }}
                  target="_blank"
                  color="primary"
                  variant="outlined"
                  onDelete={() => {}}
                  deleteIcon={<OpenInNewIcon />}
                />
              ) : (
                item.patient_name || '-'
              )}
            </TableCell>
            <TableCell>{item.doctor_name || '-'}</TableCell>
            <TableCell>{statusChip(item.status)}</TableCell>
            <TableCell>{item.codigo_validacao || '-'}</TableCell>
            <TableCell>{formatDate(item.created_at)}</TableCell>
            <TableCell>
              <Button color="second" onClick={() => openJson(item.id)}>
                <CodeIcon fontSize="small" />
                &nbsp;Ver JSON
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </Table>

      <LinearProgress loading={loading} />

      <Typography
        variant="subtitle2"
        color="primary"
        align="right"
        style={{ paddingRight: 16, marginTop: 10 }}
      >
        <strong>Total: </strong>
        {total}
      </Typography>

      <TablePagination
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={(event, newPage) => setPage(newPage)}
        onChangeRowsPerPage={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      <Dialog
        icon={<CodeIcon />}
        isOpen={jsonOpen}
        handleClose={() => setJsonOpen(false)}
        title="JSON da prescrição"
        maxWidth="md"
        dialogActions={
          <Button color="grey" onClick={() => setJsonOpen(false)}>
            Fechar
          </Button>
        }
      >
        <LinearProgress loading={jsonLoading} />
        {jsonData && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{ marginTop: 8 }}
            >
              <Typography variant="subtitle2">
                Enviado à Mevo (request)
              </Typography>
              <Button color="second" onClick={() => copyJson(jsonData.request)}>
                <FileCopyOutlined fontSize="small" />
                &nbsp;Copiar
              </Button>
            </Box>
            {jsonBox(jsonData.request)}

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{ marginTop: 16 }}
            >
              <Typography variant="subtitle2">
                Retorno / webhook (response)
              </Typography>
              <Button color="second" onClick={() => copyJson(jsonData.response)}>
                <FileCopyOutlined fontSize="small" />
                &nbsp;Copiar
              </Button>
            </Box>
            {jsonBox(jsonData.response)}
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default MevoPrescricoes;
